import { Box, styled, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { getTickInterval } from '../lib/tickSnap';

export const RULER_SIZE = 20;
const MIN_LABEL_SPACING = 80;
const DIMENSION_LABEL_GUARD = 60;

type Orientation = 'horizontal' | 'vertical';

interface StudioRulerProps {
  designSize: number;
  offset: number;
  orientation: Orientation;
  scale: number;
  scrollOffset: number;
  stageSize: number;
  zoom: number;
}

const RulerContainer = styled(Box)<{ orientation: Orientation }>(({ orientation, theme }) => ({
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  overflow: 'hidden',
  userSelect: 'none',
  ...(orientation === 'horizontal'
    ? { height: RULER_SIZE, width: '100%' }
    : { width: RULER_SIZE, height: '100%' }),
}));

export const StudioRuler = ({
  designSize,
  offset,
  orientation,
  scale,
  scrollOffset,
  stageSize,
  zoom,
}: StudioRulerProps) => {
  const theme = useTheme();
  const dimensionColor = theme.palette.primary.main;
  const dimensionTextColor = theme.palette.primary.text;
  const { major, minor } = useMemo(() => getTickInterval(zoom), [zoom]);

  const visibleStart = -offset + scrollOffset / scale;
  const visibleEnd = (stageSize + scrollOffset) / scale - offset;

  const labelInterval = useMemo(() => {
    const majorScreenSpacing = major * scale;
    if (majorScreenSpacing >= MIN_LABEL_SPACING) return major;
    const multiplier = Math.ceil(MIN_LABEL_SPACING / majorScreenSpacing);
    return major * multiplier;
  }, [major, scale]);

  const dimensionMarks = useMemo(() => new Set([0, designSize]), [designSize]);

  const ticks = useMemo(() => {
    const result: Array<{
      position: number;
      screenPos: number;
      isMajor: boolean;
      isLabeled: boolean;
      isDimension: boolean;
    }> = [];

    // Screen positions of dimension marks (for suppressing nearby regular labels)
    const dimensionScreenPositions = [...dimensionMarks].map(
      (pos) => (pos + offset) * scale - scrollOffset,
    );

    const startTick = Math.floor(visibleStart / minor) * minor;
    const endTick = Math.ceil(visibleEnd / minor) * minor;

    for (let pos = startTick; pos <= endTick; pos += minor) {
      const screenPos = (pos + offset) * scale - scrollOffset;

      if (screenPos >= 0 && screenPos <= stageSize) {
        const isMajor = pos % major === 0;
        const isDimension = dimensionMarks.has(pos);
        const wouldLabel = isMajor && pos % labelInterval === 0;

        // Suppress regular labels that are too close to a dimension mark
        const tooCloseToDimension =
          wouldLabel &&
          !isDimension &&
          dimensionScreenPositions.some((dp) => Math.abs(screenPos - dp) < DIMENSION_LABEL_GUARD);

        result.push({
          position: pos,
          screenPos,
          isMajor: isMajor || isDimension,
          isLabeled: isDimension || (wouldLabel && !tooCloseToDimension),
          isDimension,
        });
      }
    }

    // Inject dimension marks if they weren't already in the tick range
    for (const dimPos of dimensionMarks) {
      const screenPos = (dimPos + offset) * scale - scrollOffset;
      if (screenPos >= 0 && screenPos <= stageSize && !result.some((t) => t.position === dimPos)) {
        result.push({
          position: dimPos,
          screenPos,
          isMajor: true,
          isLabeled: true,
          isDimension: true,
        });
      }
    }

    return result.sort((a, b) => a.screenPos - b.screenPos);
  }, [
    visibleStart,
    visibleEnd,
    minor,
    major,
    labelInterval,
    offset,
    scale,
    scrollOffset,
    stageSize,
    dimensionMarks,
  ]);

  return (
    <RulerContainer orientation={orientation}>
      <svg
        width={orientation === 'horizontal' ? stageSize : RULER_SIZE}
        height={orientation === 'horizontal' ? RULER_SIZE : stageSize}
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        {ticks.map(({ position, screenPos, isMajor, isLabeled, isDimension }) => {
          const tickSize = isDimension ? RULER_SIZE : isMajor ? RULER_SIZE * 0.6 : RULER_SIZE * 0.3;
          const tickColor = isDimension ? dimensionColor : theme.palette.studio.tick;
          const textColor = isDimension ? dimensionTextColor : theme.palette.studio.rulerText;
          const fontWeight = isDimension ? 600 : undefined;

          if (orientation === 'horizontal') {
            return (
              <g key={position}>
                <line
                  x1={screenPos}
                  y1={RULER_SIZE}
                  x2={screenPos}
                  y2={RULER_SIZE - tickSize}
                  stroke={tickColor}
                  strokeWidth={1}
                />
                {isLabeled && (
                  <text
                    x={screenPos + 2}
                    y={2}
                    fontSize={9}
                    fill={textColor}
                    fontWeight={fontWeight}
                    dominantBaseline="hanging"
                  >
                    {position}
                  </text>
                )}
              </g>
            );
          }

          return (
            <g key={position}>
              <line
                x1={RULER_SIZE}
                y1={screenPos}
                x2={RULER_SIZE - tickSize}
                y2={screenPos}
                stroke={tickColor}
                strokeWidth={1}
              />
              {isLabeled && (
                <text
                  x={2}
                  y={screenPos + 2}
                  fontSize={9}
                  fill={textColor}
                  fontWeight={fontWeight}
                  dominantBaseline="hanging"
                  textAnchor="end"
                  transform={`rotate(-90, 2, ${screenPos + 2})`}
                >
                  {position}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </RulerContainer>
  );
};
